import { Link } from 'react-router-dom';

import { ConnectLinkedInLink } from '@/modules/hiring-manager/linkedin/ConnectLinkedInLink';
import type { LinkedInStatus } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';

interface LinkedInNoticeProps {
  status: LinkedInStatus;
  /** Where LinkedIn gets connected; absent when this workspace cannot. */
  integrationsHref?: string;
  /**
   * Finlay reaches out on its own: a missing LinkedIn only holds outreach
   * until it is connected, so the notice does not ask for it first.
   */
  isAutomated?: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * Why outreach cannot go out yet: LinkedIn is not connected, or Finlay could
 * not check. Renders nothing while the check runs or once it is connected.
 */
export function LinkedInNotice({
  status,
  integrationsHref,
  isAutomated = false,
  onRetry,
  className,
}: LinkedInNoticeProps) {
  if (status === 'pending' || status === 'connected') return null;
  if (status === 'error') {
    return (
      <p className={className}>
        Could not check your LinkedIn connection.{' '}
        <button
          className="text-primary-700 cursor-pointer font-medium hover:underline"
          onClick={onRetry}
          type="button"
        >
          Try again
        </button>
      </p>
    );
  }
  if (isAutomated && integrationsHref) {
    return (
      <p className={className}>
        Finlay sends from your LinkedIn, which isn’t connected yet. Outreach
        starts as soon as you connect it.{' '}
        <ConnectLinkedInLink to={integrationsHref} />
      </p>
    );
  }
  return (
    <p className={className}>
      Finlay sends from your LinkedIn.{' '}
      {integrationsHref ? (
        <Link
          className="text-primary-700 font-medium hover:underline"
          to={integrationsHref}
        >
          Connect it first.
        </Link>
      ) : (
        'This workspace cannot connect one.'
      )}
    </p>
  );
}
